import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowUpRight, MapPin } from 'lucide-react';
import axios from 'axios';
import Reveal from '../components/Reveal';
import { getEventIconComponent, getEventAccent } from '../lib/eventStyle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventsCalendar = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [cityFilter, setCityFilter] = useState('All');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const [upcomingRes, pastRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/events-past`),
        ]);
        setUpcomingEvents(upcomingRes.data);
        setPastEvents(pastRes.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
      setLoading(false);
    };

    fetchEvents();
  }, []);

  const allEvents = [...upcomingEvents, ...pastEvents];
  const categories = ['All', ...Array.from(new Set(allEvents.map((e) => e.type)))];
  const cities = ['All', ...Array.from(new Set(allEvents.map((e) => e.venue).filter(Boolean)))];
  const applyFilter = (list) =>
    list.filter(
      (e) => (filter === 'All' || e.type === filter) && (cityFilter === 'All' || e.venue === cityFilter)
    );
  const filteredUpcoming = applyFilter(upcomingEvents);
  const filteredPast = applyFilter(pastEvents);


  const getIcon = (type, iconName) => {
    const IconComponent = getEventIconComponent(type, iconName);
    return <IconComponent size={20} />;
  };

  const EventCard = ({ event, isPast = false, index = 0 }) => {
    const accent = getEventAccent(event.type);
    return (
    <Reveal delay={index * 80}>
      <Link
        key={event.id}
        to={`/events/${event.id}`}
        className={`group surface-2 border border-white/8 rounded-3xl overflow-hidden dark-transition dark-hover flex flex-col h-full ${
          isPast ? 'opacity-90' : ''
        }`}
        data-testid={`event-card-${event.id}`}
      >
      <div className="relative h-64 overflow-hidden">
        <img
          src={event.image}
          alt={event.name}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${
            isPast ? 'grayscale-[40%] group-hover:grayscale-0' : ''
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

        {/* Type pill (top-left) */}
        <span className={`absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase ${accent.pill}`}>
          {getIcon(event.type, event.icon)}
          {event.type}
        </span>

        {/* Past badge (top-right) */}
        {isPast && (
          <span className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium tracking-wider uppercase glass-panel text-white/80">
            Past Event
          </span>
        )}

        {/* Date overlay (bottom) */}
        <div className={`absolute bottom-4 left-4 right-4 flex items-center gap-2 ${accent.text}`}>
          <Calendar size={14} />
          <span className="body-small font-medium">{event.date}</span>
        </div>
      </div>

      <div className="p-7 flex-1 flex flex-col">
        <h3 className={`heading-1 text-white mb-5 ${accent.hoverText} transition-colors leading-tight`}>
          {event.name}
        </h3>
        <div className="mt-auto pt-5 border-t border-white/8 flex items-center justify-between">
          {!isPast && <p className={`body-large ${accent.text} font-semibold`}>{event.price}</p>}
          <span className={`inline-flex items-center gap-1.5 text-white/50 ${accent.hoverText} transition-colors text-sm font-medium`}>
            View Details
            <ArrowUpRight
              size={16}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </span>
        </div>
      </div>
    </Link>
    </Reveal>
    );
  };

  if (loading) {
    return (
      <div className="dark-container min-h-screen flex items-center justify-center" data-testid="events-loading">
        <p className="text-white">Loading events...</p>
      </div>
    );
  }

  return (
    <div className="dark-container" data-testid="events-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-radial-teal opacity-50 pointer-events-none" />
        <div className="dark-content-container relative pt-20 pb-24 lg:pt-28 lg:pb-32 text-center">
          <span className="eyebrow mb-6 block animate-fade-in">Calendar</span>
          <h1 className="display-huge text-white mb-6 animate-fade-in-delay-1" data-testid="events-title">
            Events <span className="text-[#64C8FF]">Calendar</span>
          </h1>
          <p className="body-large text-white/65 max-w-2xl mx-auto animate-fade-in-delay-2">
            Discover upcoming experiences designed to expand your network, sharpen your skills, and
            explore new interests
          </p>

          {/* Category filter */}
          {categories.length > 2 && (
            <div className="flex flex-wrap items-center justify-center gap-3 mt-10 animate-fade-in-delay-2" data-testid="events-filter-bar">
              {categories.map((cat) => {
                const active = filter === cat;
                const accentHex = cat === 'All' ? '#00FFD1' : getEventAccent(cat).hex;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setFilter(cat)}
                    data-testid={`events-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all duration-300 border ${
                      active
                        ? 'text-black'
                        : 'text-white/70 border-white/12 hover:text-white hover:border-white/30'
                    }`}
                    style={
                      active
                        ? { backgroundColor: accentHex, borderColor: accentHex }
                        : undefined
                    }
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          )}

          {/* City filter */}
          {cities.length > 2 && (
            <div className="flex flex-wrap items-center justify-center gap-2.5 mt-5 animate-fade-in-delay-2" data-testid="events-city-filter-bar">
              <span className="inline-flex items-center gap-1.5 text-white/40 text-xs font-medium tracking-wider uppercase mr-1">
                <MapPin size={13} /> City
              </span>
              {cities.map((city) => {
                const active = cityFilter === city;
                return (
                  <button
                    key={city}
                    type="button"
                    onClick={() => setCityFilter(city)}
                    data-testid={`events-city-filter-${city.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all duration-300 border ${
                      active
                        ? 'bg-[#64C8FF] border-[#64C8FF] text-black'
                        : 'text-white/60 border-white/12 hover:text-white hover:border-white/30'
                    }`}
                  >
                    {city}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* UPCOMING */}
      <section className="relative">
        <div className="dark-content-container pb-24 lg:pb-36">
          <Reveal className="flex items-end justify-between mb-12">
            <div>
              <span className="eyebrow mb-4 block">Next up</span>
              <h2 className="display-large text-white">Upcoming Events</h2>
            </div>
            <div className="hidden md:flex items-center gap-2 text-white/40">
              <span className="w-8 h-px bg-white/20" />
              <span className="body-small">{filteredUpcoming.length} listed</span>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUpcoming.length === 0 ? (
              <div className="col-span-full text-center text-white/50 py-20 surface-2 border border-white/8 rounded-3xl" data-testid="events-upcoming-empty">
                <p className="body-large">No upcoming {filter !== 'All' ? filter : ''} events{cityFilter !== 'All' ? ` in ${cityFilter}` : ''} at the moment</p>
                <p className="body-small mt-2">Check back soon for new experiences!</p>
              </div>
            ) : (
              filteredUpcoming.map((event, i) => (
                <EventCard key={event.id} event={event} isPast={false} index={i} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* PAST */}
      {filteredPast.length > 0 && (
        <section className="relative surface-1 border-y border-white/5">
          <div className="dark-content-container py-24 lg:py-36">
            <Reveal className="flex items-end justify-between mb-12">
              <div>
                <span className="eyebrow mb-4 block">Archive</span>
                <h2 className="display-large text-white">Past Events</h2>
              </div>
              <div className="hidden md:flex items-center gap-2 text-white/40">
                <span className="w-8 h-px bg-white/20" />
                <span className="body-small">{filteredPast.length} hosted</span>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPast.map((event, i) => (
                <EventCard key={event.id} event={event} isPast={true} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative">
        <div className="dark-content-container py-24 lg:py-36">
          <Reveal className="relative overflow-hidden surface-2 border border-white/10 rounded-[2.5rem] p-12 md:p-20 text-center">
            <div className="absolute inset-0 bg-grid-pattern opacity-50 pointer-events-none" />
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-radial-teal opacity-50 pointer-events-none" />
            <div className="relative">
              <span className="eyebrow mb-6 block">Shape the calendar</span>
              <h2 className="display-large text-white mb-6">
                Don't See What You're Looking For?
              </h2>
              <p className="body-large text-white/70 mb-10 max-w-2xl mx-auto">
                Members can suggest event topics, speakers, and formats. Your input shapes our
                community calendar
              </p>
              <a
                href="https://deftexp.fillout.com/lessgo"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex"
                data-testid="events-cta-join-btn"
              >
                Join Community
                <ArrowUpRight size={18} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default EventsCalendar;
